/**
 * SingleSelectRenderer Component
 * Displays single-select (radio button) questions
 */

import React from 'react';
import { FormControl, FormControlLabel, Radio, RadioGroup, Box, Typography, Link, TextField } from '@mui/material';
import type { OtherAnswerResponse, Question, ResponseValue } from '../../../types';
import QuestionHelpText from '../QuestionHelpText';

interface SingleSelectRendererProps {
  question: Question;
  selectedValue: ResponseValue | undefined;
  onSelect: (value: ResponseValue) => void;
}

function SingleSelectRenderer({
  question,
  selectedValue,
  onSelect,
}: SingleSelectRendererProps): JSX.Element {
  const helpLink = question.links?.[0];
  const otherResponse = isOtherAnswerResponse(selectedValue) ? selectedValue : undefined;
  const selectedAnswerId = otherResponse?.answerId ?? (typeof selectedValue === 'string' || typeof selectedValue === 'number' ? String(selectedValue) : '');
  const otherValue = otherResponse?.otherValue ?? '';
  const selectedAnswer = question.answers.find((answer) => String(answer.id) === String(selectedAnswerId));

  return (
    <Box className="cla-quiz-single-select" data-quiz-question-id={question.id} sx={{ mt: 1 }}>
      <QuestionHelpText description={question.metadata?.description} />
      <Typography
      className="cla-quiz-instruction"
        variant="body2"
        sx={{ mb: helpLink ? 0.5 : 2, fontStyle: 'italic', color: '#888', fontSize: '0.82rem' }}
      >
        Single select — choose one option
      </Typography>

      {helpLink && (
        <Link
          href={helpLink.url}
          target={helpLink.openInNewTab ? '_blank' : '_self'}
          rel="noopener noreferrer"
          underline="always"
          sx={{ display: 'block', mb: 2, fontSize: '0.85rem', color: '#00A9B5' }}
        >
          {helpLink.title}
        </Link>
      )}

      <FormControl className="cla-quiz-option-group" component="fieldset" fullWidth>
        <RadioGroup
          value={selectedAnswerId || ''}
          onChange={(e) => onSelect(e.target.value)}
          sx={question.metadata?.optionsLayout === 'twoColumn' ? {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 2,
            flexWrap: 'unset',
          } : undefined}
        >
          {question.answers.map((answer) => {
            const isSelected = String(selectedAnswerId) === String(answer.id);
            return (
              <FormControlLabel
                className="cla-quiz-option"
                data-quiz-answer-id={answer.id}
                key={answer.id}
                value={answer.id}
                control={
                  <Radio
                    className="cla-quiz-option-control"
                    sx={{
                      color: '#ccc',
                      '&.Mui-checked': { color: '#00A9B5' },
                    }}
                  />
                }
                label={
                  <Typography className="cla-quiz-option-label" variant="body1" sx={{ fontWeight: 500, color: '#1C3654' }}>
                    {answer.text}
                  </Typography>
                }
                sx={{
                  py: 1.5,
                  px: 2,
                  mb: 1.5,
                  border: isSelected ? '2px solid #00A9B5' : '1.5px solid #e0e0e0',
                  borderRadius: '16px',
                  bgcolor: isSelected ? '#EAF8F8' : '#fff',
                  mx: 0,
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': {
                    backgroundColor: isSelected ? '#EAF8F8' : '#f9fafb',
                    borderColor: isSelected ? '#00A9B5' : '#b0b0b0',
                  },
                }}
              />
            );
          })}
        </RadioGroup>
        {selectedAnswer?.metadata?.allowOtherValue && (
          <TextField
            label="Please specify..."
            fullWidth
            size="small"
            value={otherValue}
            onChange={(event) => onSelect({ answerId: selectedAnswerId, otherValue: event.target.value })}
            sx={{ mt: 1 }}
          />
        )}
      </FormControl>
    </Box>
  );
}

function isOtherAnswerResponse(value: ResponseValue | undefined): value is OtherAnswerResponse {
  return Boolean(
    value && typeof value === 'object' && !Array.isArray(value) &&
      typeof (value as Record<string, unknown>).answerId === 'string' &&
      typeof (value as Record<string, unknown>).otherValue === 'string',
  );
}

export default SingleSelectRenderer;
